import type { ReactNode } from 'react';
import type { TreeNode } from '../Tree.types';

/**
 * Internal node representation with computed fields.
 * NOT exported from index.ts — internal to the tree component.
 */
export interface NodeState<T = unknown> {
  /** Unique identifier */
  id: string;
  /** Visible text label */
  label: string;
  /** Selection state */
  checked: boolean;
  /** Whether the node is interactive */
  disabled: boolean;
  /** Computed: true when children have mixed checked states */
  indeterminate: boolean;
  /** Parent node ID, null for root nodes */
  parentId: string | null;
  /** IDs of direct children */
  childIds: string[];
  /** Depth in the tree (0 = root) */
  depth: number;
  /** Optional icon */
  icon?: ReactNode;
  /** Arbitrary payload */
  data?: T;
}

/**
 * Clone a NodeState to avoid mutating the original.
 */
function cloneNode<T>(node: NodeState<T>): NodeState<T> {
  return { ...node, childIds: [...node.childIds] };
}

/**
 * Create a shallow clone of the entire Map with cloned NodeState entries.
 */
function cloneMap<T>(nodeMap: Map<string, NodeState<T>>): Map<string, NodeState<T>> {
  const result = new Map<string, NodeState<T>>();
  for (const [id, node] of nodeMap) {
    result.set(id, cloneNode(node));
  }
  return result;
}

/**
 * Build a flat Map from a tree array.
 * Computes parentId, childIds, depth for each node.
 * Performs a DFS traversal to populate all computed fields.
 */
export function buildNodeMap<T>(nodes: TreeNode<T>[]): Map<string, NodeState<T>> {
  const map = new Map<string, NodeState<T>>();

  function walk(treeNodes: TreeNode<T>[], parentId: string | null, depth: number): void {
    for (const node of treeNodes) {
      const childIds = (node.children ?? []).map((c) => c.id);
      const state: NodeState<T> = {
        id: node.id,
        label: node.label,
        checked: node.checked,
        disabled: node.disabled ?? false,
        indeterminate: false,
        parentId,
        childIds,
        depth,
        icon: node.icon,
        data: node.data,
      };
      map.set(node.id, state);

      if (node.children && node.children.length > 0) {
        walk(node.children, node.id, depth + 1);
      }
    }
  }

  walk(nodes, null, 0);
  return map;
}

/**
 * Cascade a checked state change downward to all enabled descendants.
 * Disabled nodes are SKIPPED — they retain their current state.
 * Returns a new Map with updated states.
 */
export function cascadeDown<T>(
  nodeId: string,
  checked: boolean,
  nodeMap: Map<string, NodeState<T>>
): Map<string, NodeState<T>> {
  const result = cloneMap(nodeMap);

  function dfs(id: string): void {
    const node = result.get(id);
    if (!node) return;

    if (!node.disabled) {
      node.checked = checked;
    }

    for (const childId of node.childIds) {
      dfs(childId);
    }
  }

  dfs(nodeId);
  return result;
}

/**
 * Propagate state change upward through all ancestors.
 * Each ancestor recalculates checked/indeterminate from its children.
 * Returns a new Map with updated ancestor states.
 */
export function propagateUp<T>(
  nodeId: string,
  nodeMap: Map<string, NodeState<T>>
): Map<string, NodeState<T>> {
  const result = cloneMap(nodeMap);
  const startNode = result.get(nodeId);
  if (!startNode) return result;

  let currentParentId = startNode.parentId;

  while (currentParentId !== null) {
    const parent = result.get(currentParentId);
    if (!parent) break;

    const children = parent.childIds.map((cid) => result.get(cid)!);
    const allChecked = children.every((c) => c.checked && !c.indeterminate);
    const noneChecked = children.every((c) => !c.checked && !c.indeterminate);

    if (allChecked) {
      parent.checked = true;
      parent.indeterminate = false;
    } else if (noneChecked) {
      parent.checked = false;
      parent.indeterminate = false;
    } else {
      parent.checked = false;
      parent.indeterminate = true;
    }

    currentParentId = parent.parentId;
  }

  return result;
}

/**
 * Flatten the tree into a pre-order depth-first array.
 * Used for keyboard navigation and imperative API.
 */
export function flattenTree<T>(
  nodeMap: Map<string, NodeState<T>>,
  rootIds: string[]
): NodeState<T>[] {
  const result: NodeState<T>[] = [];

  function dfs(id: string): void {
    const node = nodeMap.get(id);
    if (!node) return;
    result.push(node);
    for (const childId of node.childIds) {
      dfs(childId);
    }
  }

  for (const rootId of rootIds) {
    dfs(rootId);
  }

  return result;
}

/**
 * Get visible nodes considering expand/collapse state.
 * A node is visible if all its ancestors are expanded.
 */
export function getVisibleNodes<T>(
  nodeMap: Map<string, NodeState<T>>,
  rootIds: string[],
  expandedIds: Set<string>
): NodeState<T>[] {
  const result: NodeState<T>[] = [];

  function dfs(id: string): void {
    const node = nodeMap.get(id);
    if (!node) return;
    result.push(node);

    // Only recurse into children if this node is expanded
    if (expandedIds.has(id)) {
      for (const childId of node.childIds) {
        dfs(childId);
      }
    }
  }

  for (const rootId of rootIds) {
    dfs(rootId);
  }

  return result;
}

/**
 * Diff current checked states against original.
 * Returns nodes whose checked state changed since initialization.
 */
export function diffNodes<T>(
  current: Map<string, NodeState<T>>,
  original: Map<string, boolean>
): NodeState<T>[] {
  const changed: NodeState<T>[] = [];

  for (const [id, node] of current) {
    const originalChecked = original.get(id);
    if (originalChecked !== undefined && node.checked !== originalChecked) {
      changed.push(node);
    }
  }

  return changed;
}

/**
 * Reconstruct TreeNode[] from the flat nodeMap.
 * Used to convert internal state back to public API format.
 */
export function toTreeNodes<T>(
  nodeMap: Map<string, NodeState<T>>,
  rootIds: string[]
): TreeNode<T>[] {
  function buildNode(id: string): TreeNode<T> {
    const state = nodeMap.get(id)!;
    const treeNode: TreeNode<T> = {
      id: state.id,
      label: state.label,
      checked: state.checked,
    };

    if (state.disabled) {
      treeNode.disabled = state.disabled;
    }

    if (state.icon !== undefined) {
      treeNode.icon = state.icon;
    }

    if (state.data !== undefined) {
      treeNode.data = state.data;
    }

    if (state.childIds.length > 0) {
      treeNode.children = state.childIds.map((childId) => buildNode(childId));
    }

    return treeNode;
  }

  return rootIds.map((rootId) => buildNode(rootId));
}
